import React, { useEffect } from 'react';
import PropTypes, { bool, func, object, string } from 'prop-types';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Container from 'react-bootstrap/Container';
import FormGroup from 'react-bootstrap/FormGroup';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import FormLabel from 'react-bootstrap/FormLabel';
import FormInput from '../../components/Form/FormInput';
import { postExternalContact, putExternalContact } from './slice';
import { TextMaskCustom } from '../Patient/PatientProfile';
import Form from 'react-bootstrap/Form';
import { usePracticeProviderContext } from '../../context/PracticeProviderContext';
import * as yup from 'yup';
import { SaveButton, CloseButton } from '../../components/ActionButtons';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  title: '',
  specialty: '',
  companyName: '',
  type: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  phone: '',
  fax: '',
  officeContact: '',
};

function ExternalContactForm({ onSaveCb, edit, contact, onCancelCb }) {
  const { provider: providerId } = usePracticeProviderContext();
  const dispatch = useDispatch();
  const { handleSubmit, errors, control, watch, reset } = useForm({
    defaultValues: initialState,
    mode: 'onSubmit',
  });

  const watchType = watch('type', '');

  useEffect(() => {
    if (edit && contact) {
      reset({ ...initialState, ...contact });
    }
    return () => {
      reset(initialState);
    };
  }, [edit, contact, reset]);

  // TODO: Add userId to the formData
  function handleOnSubmit(formData) {
    // added to take care of enum issue
    if (!formData.type) {
      delete formData.type;
    }

    if (edit) {
      dispatch(putExternalContact({ formData: { ...formData, userId: providerId }, callbackFn: onSaveCb, contactId: contact.id }));
    } else {
      dispatch(postExternalContact({ formData: { ...formData, userId: providerId }, callbackFn: onSaveCb }));
    }
  }

  return (
    <Container fluid>
      <Container fluid>
        <Controller
          control={control}
          name={'title'}
          render={({ onChange, value, onBlur }, { invalid }) => (
            <FormGroup controlId="title">
              <FormLabel>Title</FormLabel>
              <Select
                label={'Title'}
                clearable={false}
                onBlur={onBlur}
                onChange={(e) => onChange(e.value)}
                value={value}
                options={[
                  { label: 'Dr.', value: 'Dr.' },
                  { label: 'Mr.', value: 'Mr.' },
                  { label: 'Ms.', value: 'Ms.' },
                ]}
              />
              {errors.title && <Form.Text className={'text-danger'}>This field is required</Form.Text>}
            </FormGroup>
          )}
        />
        <Controller
          control={control}
          rules={{ required: true }}
          name={'firstName'}
          render={({ onChange, value, onBlur }, { invalid }) => <FormInput isInvalid={invalid} required={true} onBlur={onBlur} onChange={onChange} value={value} label={'*First Name'} />}
        />
        <Controller
          control={control}
          rules={{ required: true }}
          name={'lastName'}
          render={({ onChange, value, onBlur }, { invalid }) => <FormInput isInvalid={invalid} required={true} onBlur={onBlur} onChange={onChange} value={value} label={'*Last Name'} />}
        />
        <Controller
          control={control}
          name={'email'}
          rules={{
            validate: (value) => yup.string().required().email().isValidSync(value),
          }}
          render={({ onChange, value, onBlur }, { invalid }) => <FormInput isInvalid={invalid} onBlur={onBlur} onChange={onChange} value={value} label={'*Email'} />}
        />
        <Controller
          control={control}
          name={'type'}
          defaultValue={null}
          render={({ onChange, value, onBlur }) => (
            <FormGroup controlId="referral-type">
              <FormLabel>Referral Type</FormLabel>
              <Select
                label={'Referral Type'}
                onBlur={onBlur}
                required={true}
                onChange={(e) => {
                  if (e) onChange(e.value);
                }}
                value={value}
                options={[
                  { label: 'Provider', value: 'Provider' },
                  { label: 'Attorney', value: 'Attorney' },
                  { label: 'Adjuster', value: 'Adjuster' },
                ]}
              />
            </FormGroup>
          )}
        />
        {watchType === 'Adjuster' && (
          <Controller control={control} name={'companyName'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'Company Name'} />} />
        )}
        {watchType === 'Attorney' && (
          <>
            <Controller
              control={control}
              name={'subType'}
              defaultValue={null}
              render={({ onChange, value, onBlur }) => (
                <FormGroup controlId="referral-type">
                  <FormLabel>Sub Type</FormLabel>
                  <Select
                    label={'Referral Type'}
                    onBlur={onBlur}
                    required={true}
                    onChange={(e) => {
                      if (e) onChange(e.value);
                    }}
                    value={value}
                    options={[
                      { label: 'Applicant', value: 'Applicant' },
                      { label: 'Defense', value: 'Defense' },
                    ]}
                  />
                </FormGroup>
              )}
            />
            <Controller control={control} name={'firmName'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'Firm Name'} />} />
          </>
        )}
        {watchType === 'Provider' && (
          <Controller control={control} name={'specialty'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'Specialty'} />} />
        )}
      </Container>
      <h4 className={'my-4'} style={{ color: '#4a4a4a' }}>
        Address
      </h4>
      <Container fluid>
        <Row>
          <Col sm={6}>
            <Controller control={control} name={'address.street'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'Street'} />} />
          </Col>
          <Col sm={6}>
            <Controller control={control} name={'address.city'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'City'} />} />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <Controller control={control} name={'address.state'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'State'} />} />
          </Col>
          <Col sm={6}>
            <Controller control={control} name={'address.zip'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label={'Zip'} />} />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            <FormLabel>Phone</FormLabel>
            <Controller
              control={control}
              name={'phone'}
              rules={{
                validate: (value) => !value || value.length === 10,
              }}
              as={<TextMaskCustom isInvalid={errors.phone} />}
            />
          </Col>
          <Col sm={6}>
            <FormLabel>Fax</FormLabel>
            <Controller
              control={control}
              name={'fax'}
              rules={{
                validate: (value) => !value || value.length === 10,
              }}
              as={<TextMaskCustom isInvalid={errors.fax} />}
            />
          </Col>
        </Row>
        <hr />
        <Controller control={control} name={'officeContact'} render={({ onChange, value, onBlur }) => <FormInput onBlur={onBlur} onChange={onChange} value={value} label="Office Contact Person" />} />
      </Container>
      <div>
        <SaveButton className={'mr-3'} onClick={handleSubmit(handleOnSubmit)}>
          Save
        </SaveButton>
        <CloseButton onClick={onCancelCb} btnText={'Cancel & Close'} />
      </div>
    </Container>
  );
}

ExternalContactForm.propTypes = {
  onCancelCb: func,
  onSaveCb: func,
  edit: bool,
  contact: object,
};
ExternalContactForm.defaultProps = {};

export default ExternalContactForm;
